import { uniq } from "../utils/lodash-replacements";
import * as React from "react";
import { useActiveFBOs } from "../containers/ActiveFBOContainer";
import { useApi } from "../containers/ApiContainer";
import { Tenant } from "../types";
import { defaultPreferenceForAircraft, uuidv4 } from "../utils";
import { fetchAircraftWithCache } from "./api";
import useSyncToTable from "./useSyncToTable";

export const useTenants = () => {
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();
  const [tenants, setTenants] = React.useState<Tenant[]>();
  const [ready, setReady] = React.useState<boolean>(false);

  const fetchTenantsForFbo = React.useCallback(async () => {
    if (!activeFBO?.id) {
      setTenants([]);
      setReady(true);
      return;
    }

    const { data: tenantsRaw } = await postgrest
      .from("tenant")
      .select()
      .eq("fbo_id", activeFBO.id)
      .order("last_hangared", { ascending: false, nullsFirst: false });

    if (!tenantsRaw) {
      setTenants([]);
      setReady(true);
      return;
    }

    const aircraftIds = uniq(tenantsRaw.map(({ aircraft_id }) => aircraft_id));
    const aircrafts = await fetchAircraftWithCache(
      postgrest,
      aircraftIds,
      activeFBO
    );

    const tenants: Tenant[] = tenantsRaw.map((tenant) => ({
      ...tenant,
      id: tenant.id,
      placement_id: tenant.placement_id || uuidv4(), // Only generate UUID if not already present
      aircraft: aircrafts.find(({ id }) => tenant.aircraft_id === id) ?? {},
      selected: false,
      preferences:
        tenant.preferences ??
        defaultPreferenceForAircraft(
          aircrafts.find(({ id }) => tenant.aircraft_id === id),
          activeFBO
        ),
      position: {
        x: null,
        y: null,
        angle: null,
      },
    }));

    setTenants(tenants);
    setReady(true);
  }, [activeFBO, postgrest]);

  // grab the hangar to start
  React.useEffect(() => {
    fetchTenantsForFbo();
  }, [fetchTenantsForFbo]);

  const setTenant = React.useCallback(
    (tenant: Tenant) => {
      setTenants((prevTenants) => {
        // if we're updating an existing tenant, then mutate the existing tenants
        if (prevTenants.find((t) => t.id === tenant.id)) {
          return prevTenants.map((t) => (t.id === tenant.id ? tenant : t));
        } else {
          // otherwise it's a new tenant, so add it
          return [tenant].concat(prevTenants);
        }
      });
    },
    [] // No dependencies needed since we use the functional update pattern
  );

  useSyncToTable<Tenant[]>({
    thingToWatch: tenants,
    tableName: "tenant",
  });

  return {
    ready,
    tenants: tenants ?? [],
    setTenant,
    setTenants,
  };
};
